<template>
  <div>
    <form
      :action="redirectPage"
      :class="{ submitted: form_submitted }"
      method="get"
      @formdata="(e) => onFormData(e.formData)"
      @keyup.enter="(e) => onEnterKeyup(e, onSubmit)"
    >
      <div :class="variantClass" class="variant">
        <HeadlineWithIcon
          v-if="!reduced"
          :icon="variant"
          :headline="headline"
          :theme="theme"
        />
        <PromoBadge :text="badge" v-if="badge && !reduced" />
        <div class="inner">
          <div
            class="column-adaptable-33-50 order-adaptable-1"
            v-if="customerOptions === 'b2c_b2b' && !reduced"
          >
            <CustomerTypeSelection
              :componentId="componentId + '_' + variant"
              v-model="formFields.customer"
              :options="CUSTOMER_TYPES_OPTIONS"
              :theme="theme"
            />
          </div>

          <div :class="columnClass" class="order-adaptable-3">
            <div class="form-element">
              <ZipCodeWithAutocomplete
                v-if="isShown"
                v-model:postcode="formFields.postcode"
                v-model:city="formFields.city"
                :error="hasZipError(validation_errors)"
                @input="onFieldInput('postcode')"
                ref="postcode"
              />
            </div>
          </div>

          <div v-if="!reduced" class="column-adaptable-33-50 order-adaptable-2">
            <div class="mid-section" v-if="formFields.customer === '01'">
              <p v-html="CALC_LANG.labels.persons_count"></p>
              <Slider
                data-private-info="true"
                v-model="formFields.consumption"
                :min="PERSONS_COUNT_CONSUMPTION_MIN"
                :max="PERSONS_COUNT_CONSUMPTION_MAX"
                :step="500"
                theme="dark"
              />
              <IconSelectorPersonsCount
                v-model="formFields.consumption"
                :steps="PERSONS_COUNT_CONSUMPTION"
                :theme="theme"
              />
            </div>

            <div class="mid-section" v-if="formFields.customer === '02'">
              <p v-html="CALC_LANG.labels.business_size"></p>

              <Slider
                data-private-info="true"
                v-model="formFields.consumption_industry"
                :min="BUSINESS_SIZE_CONSUMPTION_MIN"
                :max="BUSINESS_SIZE_CONSUMPTION_MAX"
                :step="1000"
                theme="dark"
              />
              <IconSelectorBusinessSize
                v-model="formFields.consumption_industry"
                :min="BUSINESS_SIZE_CONSUMPTION_MIN"
                :max="BUSINESS_SIZE_CONSUMPTION_MAX"
              />
            </div>
          </div>

          <div :class="columnClass" class="order-adaptable-4">
            <div class="form-element" v-if="formFields.customer === '01'">
              <TextInput
                v-model="formFields.consumption"
                name="consumption"
                ref="consumption"
                :is-number="true"
                :placeholder="CALC_LANG.labels.consumption"
                :disabled="formFields.customer !== '01'"
                :validate-input="
                  () =>
                    validation_errors?.consumption?.length > 0
                      ? validation_errors?.consumption[0]
                      : true
                "
                :maxlength="7"
                data-private-info="true"
                @change="onFieldInput('consumption')"
                custom-error-color="var(--SysDarkError)"
              />
            </div>

            <div class="form-element" v-if="formFields.customer === '02'">
              <TextInput
                v-model="formFields.consumption_industry"
                ref="consumption_industry"
                :is-number="true"
                :placeholder="CALC_LANG.labels.consumption"
                :disabled="formFields.customer !== '02'"
                :validate-input="
                  () =>
                    validation_errors?.consumption_industry?.length > 0
                      ? validation_errors?.consumption_industry[0]
                      : true
                "
                @change="onFieldInput('consumption_industry')"
                :maxlength="7"
                data-private-info="true"
                custom-error-color="var(--SysDarkError)"
              />
            </div>
          </div>
        </div>

        <BasicButton
          native-type="submit"
          button-type="filled"
          :label="CALC_LANG.buttons.power"
          @button-click="(e) => onSubmitClick(e, onSubmit)"
        />

        <input name="calculator" :value="calculatorType" type="hidden" />
        <input name="customer" v-model="formFields.customer" type="hidden" />
        <input name="disableVouchers" :value="disableVouchers" type="hidden" />
        <input
          name="promotion_code"
          v-if="promotionCode !== ''"
          :value="promotionCode"
          type="hidden"
        />
        <input name="onlyThisPage" :value="onlyThisPage" type="hidden" />
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, toRaw, watch } from 'vue';
import HeadlineWithIcon from '../_shared/HeadlineWithIcon/HeadlineWithIcon.vue';
import PromoBadge from '../_shared/PromoBadge/PromoBadge.vue';
import CustomerTypeSelection from '../_shared/CustomerTypeSelection/CustomerTypeSelection.vue';
import ZipCodeWithAutocomplete from 'ewi-ui/components/3-organisms/ZipCodeWithAutocomplete/ZipCodeWithAutocomplete.vue';
import IconSelectorPersonsCount from '../_shared/IconSelectorPersonsCount/IconSelectorPersonsCount.vue';
import IconSelectorBusinessSize from '../_shared/IconSelectorBusinessSize/IconSelectorBusinessSize.vue';
import BasicButton from 'ewi-ui/components/1-atoms/BasicButton/BasicButton.vue';
import TextInput from 'ewi-ui/components/1-atoms/VForm/TextInput/TextInput.vue';
import Slider from 'ewi-ui/components/1-atoms/VForm/Slider/Slider.vue';
import {
  computeFormHasValidationErrors,
  onSubmitClick,
  onEnterKeyup,
  hasZipError,
  initFromStorage,
} from '../helpers.js';
import {
  CUSTOMER_TYPES,
  PERSONS_COUNT_CONSUMPTION,
  PERSONS_COUNT_CONSUMPTION_DEFAULT,
  BUSINESS_SIZE_CONSUMPTION,
  BUSINESS_SIZE_CONSUMPTION_DEFAULT,
  CUSTOMER_TYPES_OPTIONS,
} from '../constants.js';
import { CALC_LANG } from '../translations.js';
import {
  validateForm,
  validateField,
  stringifyFormData,
} from 'ewi-ui-helpers/FormUtils/FormValidator.js';
import { CALCULATOR_POWER_SCHEMA } from 'ewi-ui-helpers/FormUtils/ValidationSchemas/calculator.js';
import { calculatorStore } from '../_store/calculator.js';

const props = defineProps({
  customerOptions: String,
  componentId: String,
  promotionCode: String,
  disableVouchers: String,
  onlyThisPage: String,
  redirectPage: String,
  badge: String,
  theme: String,
  variant: String,
  headline: String,
  calculatorType: String,
  reduced: Boolean,
  isShown: Boolean,
});

const variantClass = computed(() => (props.reduced ? 'variant--reduced' : ''));

const columnClass = computed(() =>
  props.reduced ? 'column-half' : 'column-adaptable-33-50',
);

const store = calculatorStore();

const formFields = reactive({
  postcode: '',
  consumption: PERSONS_COUNT_CONSUMPTION_DEFAULT,
  consumption_industry: BUSINESS_SIZE_CONSUMPTION_DEFAULT,
  customer: CUSTOMER_TYPES[props.customerOptions]?.defaultValue,
  city: '',
});

const validation_errors = reactive({});
const form_dirty = ref(false);
const form_submitted = ref(false);
const postcode = ref(null);
const consumption = ref(null);
const consumption_industry = ref(null);

// Constants
const PERSONS_COUNT_CONSUMPTION_MIN = parseInt(PERSONS_COUNT_CONSUMPTION[0]);
const PERSONS_COUNT_CONSUMPTION_MAX = parseInt(
  PERSONS_COUNT_CONSUMPTION[PERSONS_COUNT_CONSUMPTION.length - 1],
);
const BUSINESS_SIZE_CONSUMPTION_MIN = parseInt(BUSINESS_SIZE_CONSUMPTION[0]);
const BUSINESS_SIZE_CONSUMPTION_MAX = parseInt(
  BUSINESS_SIZE_CONSUMPTION[BUSINESS_SIZE_CONSUMPTION.length - 1],
);

// Methods
const onFieldInput = (field) => {
  if (!form_dirty.value) return;
  let state = createState();
  if (field.includes('consumption')) {
    state.set(field, parseInt(state.get(field)) ?? 0);
  }

  const errors_count_before = validation_errors[field]?.length;

  validation_errors[field] = validateField(
    CALCULATOR_POWER_SCHEMA,
    field,
    state,
  );
  const errors_count_after = validation_errors[field].length;
  if (field.includes('consumption')) {
    validateInputs();
  }
  if (errors_count_after < errors_count_before) {
    Object.assign(
      validation_errors,
      validateForm(CALCULATOR_POWER_SCHEMA, state),
    );
  }
};

const onFormData = (formData) => {
  formData.delete('customer_' + props.componentId + '_' + props.variant);
  formData.append('postcode', formFields.postcode);
  formData.append('city', formFields.city);

  if (formFields.customer === '01') {
    formData.append('consumption', formFields.consumption);
    formData.delete('consumption_industry');
  } else {
    formData.append('consumption_industry', formFields.consumption_industry);
    formData.delete('consumption');
  }
  let formDataAsString = stringifyFormData(formData);

  store.saveFormData(
    props.calculatorType,
    props.customerOptions,
    formDataAsString,
  );
};

const createState = () => {
  const newState = new Map(Object.entries(toRaw(formFields)));

  if (newState.get('customer') === '01') {
    newState.delete('consumption_industry');
  } else {
    newState.delete('consumption');
  }
  return newState;
};

const onSubmit = (form) => {
  form_dirty.value = true;
  const state = createState();
  onFormData(new FormData(form));
  Object.assign(
    validation_errors,
    validateForm(CALCULATOR_POWER_SCHEMA, state),
  );

  if (!formHasValidationErrors.value) {
    form_submitted.value = true;
    form.submit();
  } else {
    validateInputs();
  }
};

const validateInputs = (value) => {
  if (validation_errors.consumption) {
    consumption.value?.validate();
  }
  if (validation_errors.consumption_industry) {
    consumption_industry.value?.validate();
  }
};

// Computed
const formHasValidationErrors = computeFormHasValidationErrors(
  validation_errors,
  { postcode, consumption, consumption_industry },
);

const cityFromPostCode = computed(() => formFields.city);

// Watchers
watch(cityFromPostCode, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    form_dirty.value = true;
    onFieldInput('city');
  }
});

onMounted(() => {
  if (formFields.customer === '') {
    formFields.customer = CUSTOMER_TYPES[props.customerOptions]?.defaultValue;
  }

  form_submitted.value = false;
  Object.assign(formFields, initFromStorage(formFields, store, props));
});
</script>
